// src/lib/gps.js
// Provider-agnostic GPS helpers for the on-course rangefinder. No map library
// here — just the coordinate model, distance math, and a geolocation watcher.
// The map view (Mapbox) layers on top of this.

import { Capacitor } from '@capacitor/core'
import { Geolocation } from '@capacitor/geolocation'

// GolfAPI point-of-interest codes (see course_coordinates migration legend).
export const POI = {
  GREEN: 1, GREEN_BUNKER: 2, FAIRWAY_BUNKER: 3, WATER: 4, TREES: 5,
  MARK_100: 6, MARK_150: 7, MARK_200: 8, DOGLEG: 9, ROAD: 10,
  FRONT_TEE: 11, BACK_TEE: 12,
}
// location: 1 front, 2 middle, 3 back
export const LOC = { FRONT: 1, MIDDLE: 2, BACK: 3 }

export const POI_LABEL = {
  1: 'Green', 2: 'Green bunker', 3: 'Fairway bunker', 4: 'Water', 5: 'Trees',
  6: '100', 7: '150', 8: '200', 9: 'Dogleg', 10: 'Road', 11: 'Front tee', 12: 'Back tee',
}

const EARTH_M = 6371000 // mean earth radius, meters
const M_TO_YD = 1.09361
const M_TO_FT = 3.28084
const toRad = (d) => (d * Math.PI) / 180

// Great-circle distance in METERS between two lat/lng points (shared core).
function _meters(lat1, lng1, lat2, lng2) {
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_M * Math.asin(Math.min(1, Math.sqrt(a)))
}

// Great-circle distance in YARDS between two lat/lng points.
export function distanceYards(lat1, lng1, lat2, lng2) {
  if ([lat1, lng1, lat2, lng2].some((v) => v == null || Number.isNaN(Number(v)))) return null
  return Math.round(_meters(lat1, lng1, lat2, lng2) * M_TO_YD)
}

// Great-circle distance in FEET — for putt-length readouts where whole-yard
// rounding is too coarse (a 12-ft putt would round to 0 yds → "0").
export function distanceFeet(lat1, lng1, lat2, lng2) {
  if ([lat1, lng1, lat2, lng2].some((v) => v == null || Number.isNaN(Number(v)))) return null
  return Math.round(_meters(lat1, lng1, lat2, lng2) * M_TO_FT)
}

// Initial compass bearing (degrees clockwise from north, 0–360) from point 1 to
// point 2. Used to orient the map so the tee→green line points "up".
export function bearingDeg(lat1, lng1, lat2, lng2) {
  if ([lat1, lng1, lat2, lng2].some((v) => v == null || Number.isNaN(Number(v)))) return 0
  const φ1 = toRad(lat1), φ2 = toRad(lat2)
  const Δλ = toRad(lng2 - lng1)
  const y = Math.sin(Δλ) * Math.cos(φ2)
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ)
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360
}

// Pull the front/middle/back green points for a hole from a coordinates array
// (rows: { poi, location, side_fw, hole, latitude, longitude }).
export function greenPoints(coords, hole) {
  const greens = (coords || []).filter((c) => c.poi === POI.GREEN && Number(c.hole) === Number(hole))
  const pick = (loc) => {
    const g = greens.find((c) => c.location === loc)
    return g ? { lat: Number(g.latitude), lng: Number(g.longitude) } : null
  }
  // Fall back to any green point if front/middle/back aren't tagged.
  const any = greens[0] ? { lat: Number(greens[0].latitude), lng: Number(greens[0].longitude) } : null
  return {
    front: pick(LOC.FRONT) || any,
    middle: pick(LOC.MIDDLE) || any,
    back: pick(LOC.BACK) || any,
  }
}

// Distances (yards) from a player position to a hole's green front/middle/back.
export function distancesToGreen(playerLat, playerLng, coords, hole) {
  const g = greenPoints(coords, hole)
  const d = (pt) => (pt ? distanceYards(playerLat, playerLng, pt.lat, pt.lng) : null)
  return { front: d(g.front), middle: d(g.middle), back: d(g.back), greenCenter: g.middle }
}

// Wrap navigator.geolocation.watchPosition as a SINGLETON: one underlying
// browser watch shared by all subscribers, so we never trigger two permission
// prompts (StrictMode double-mount, per-hole effect restart, etc.). Subscribing
// returns an unsubscribe(); the underlying watch stops a short while after the
// last subscriber leaves, so a quick unmount/remount reuses it (no re-prompt).
let _geoWatchId = null
let _geoSubs = new Set()
let _geoStopTimer = null
let _geoLast = null

// Start the single shared underlying browser watch. Extracted so both the
// synchronous (web/iOS) path and the Android permission-gated async path reuse
// exactly the same watch setup. No-ops if a watch is already live or if every
// subscriber left while an async permission request was in flight.
function _startUnderlyingWatch() {
  if (_geoWatchId != null) return
  if (_geoSubs.size === 0) return
  if (typeof navigator === 'undefined' || !navigator.geolocation) return
  _geoWatchId = navigator.geolocation.watchPosition(
    (pos) => {
      _geoLast = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }
      _geoSubs.forEach((s) => s.onUpdate(_geoLast))
    },
    (err) => { _geoSubs.forEach((s) => s.onError?.(err)) },
    // maximumAge:0 — never hand back a cached fix; the on-course dot must
    // reflect the player's live position. Smoothing/gating happens downstream
    // (makeGpsFilter) rather than by tolerating stale fixes here.
    { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 },
  )
}

export function watchPosition(onUpdate, onError) {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    onError?.(new Error('Geolocation not available'))
    return () => {}
  }
  const sub = { onUpdate, onError }
  _geoSubs.add(sub)
  if (_geoLast) onUpdate(_geoLast) // hand the new subscriber the last fix immediately
  if (_geoStopTimer) { clearTimeout(_geoStopTimer); _geoStopTimer = null }
  if (_geoWatchId == null) {
    // On Android the wrapped (Capacitor) WebView does NOT surface the OS
    // location prompt for navigator.geolocation on its own (D-330), so GPS
    // silently fails. Trigger the native permission request first; once
    // granted, Capacitor auto-grants navigator.geolocation for the app origin
    // and the existing watch below works unchanged. Fire-and-forget so
    // watchPosition still returns its unsubscribe closure SYNCHRONOUSLY
    // (HoleMap.jsx relies on `const stop = watchPosition(...)`). Gated to
    // Android only: iOS already prompts via navigator.geolocation + Info.plist,
    // so calling the plugin there risks a double prompt. Web is untouched.
    if (Capacitor.getPlatform() === 'android') {
      Geolocation.requestPermissions()
        // Denial or throw: fall through to the normal watch/onError path below
        // so nothing crashes and existing error handling still runs.
        .catch(() => {})
        .then(() => { _startUnderlyingWatch() })
    } else {
      _startUnderlyingWatch()
    }
  }
  return () => {
    _geoSubs.delete(sub)
    if (_geoSubs.size === 0 && _geoWatchId != null) {
      _geoStopTimer = setTimeout(() => {
        if (_geoSubs.size === 0 && _geoWatchId != null) {
          navigator.geolocation.clearWatch(_geoWatchId)
          _geoWatchId = null
        }
      }, 3000)
    }
  }
}

// Force a fresh underlying watch WITHOUT touching subscribers. Android in
// particular can silently stop delivering position updates while the PWA is
// backgrounded — `_geoWatchId` stays non-null so watchPosition thinks it's
// still active, but no callbacks ever arrive. On visibilitychange (visible),
// HoleMap calls this to guarantee a live watch. Preserves the last-known
// fix so subscribers keep their current dot position until a fresh one
// arrives.
export function restartGeoWatch() {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return
  if (_geoStopTimer) { clearTimeout(_geoStopTimer); _geoStopTimer = null }
  if (_geoWatchId != null) {
    try { navigator.geolocation.clearWatch(_geoWatchId) } catch { /* clearWatch is best-effort */ }
    _geoWatchId = null
  }
  if (_geoSubs.size === 0) return
  _geoWatchId = navigator.geolocation.watchPosition(
    (pos) => {
      _geoLast = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }
      _geoSubs.forEach((s) => s.onUpdate(_geoLast))
    },
    (err) => { _geoSubs.forEach((s) => s.onError?.(err)) },
    { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 },
  )
}

// ── On-course thresholds ───────────────────────────────────────────────────
// Naming convention: <ZONE>_<IN|OUT|...>_YDS. The "_OUT" variants exist where
// the zone has hysteresis (small dead-band so GPS jitter at the boundary
// doesn't flicker the state). Where a zone has no flicker risk it's a single
// threshold with no _OUT pair.

// Auto-zoom — engages only when the player is within this of the green FRONT.
// Outside it, the initial geometric hole fit stays put.
export const AUTOZOOM_OUTER_YDS = 300

// Step re-framing — between AUTOZOOM_OUTER and STEP_STOP, the camera re-frames
// every STEP_INTERVAL yards closer the player gets (measured to green front).
export const STEP_INTERVAL_YDS = 50
export const STEP_STOP_YDS = 50        // stop stepping (to green front)

// Approach zone (to green CENTER). Entering the zone collapses the aim target
// down to its chip and pins the green-distances box. Small +5 yd hysteresis on
// exit to avoid a player standing right at the boundary flickering the target.
// Threshold is 75 yds per CLAUDE.md standing spec ("stopping at the approach
// view at 75 yds"). A pre-2026-07 commit briefly bumped this to 165 which
// hid the aim line from a full wedge out on every non-short hole — reverted.
export const APPROACH_IN_YDS = 75
export const APPROACH_OUT_YDS = 80

// Putting view (to green CENTER). Hard zoom-in on the green for the
// approach/putting view. Big +30 yd hysteresis because the camera change is
// dramatic and players near the green move slowly.
export const PUTTING_VIEW_IN_YDS = 40
export const PUTTING_VIEW_OUT_YDS = 70

// Tee proximity (to tee POI). Inside this, the "## yds from Tee" readout
// shows, the mark-tee chips control appears, and tapping a tee POI on the
// map anchors yardage + aim from that tee.
export const NEAR_TEE_YDS = 50
// Fallback proximity for the mark-tee button on holes with incomplete tee
// POI data (< 2 GolfAPI tee POIs). Lets the player drop the missing tee
// from a wider radius — e.g., hole 6 has only the back tee, so the player
// walking to where the front tee SHOULD be is 60+ yds from the back marker
// and would otherwise be locked out of marking the front tee.
export const NEAR_TEE_FALLBACK_YDS = 150

// Target bullseye re-projection — when an auto-step re-frame happens, the
// aim target can end up too close to (or behind) the player. If the target's
// distance from the player is below TARGET_MIN_RATIO of the player→green
// distance, we snap it forward to TARGET_RESET_RATIO of that distance.
export const TARGET_MIN_RATIO = 0.5
export const TARGET_RESET_RATIO = 0.7

// ── Crowdsourced fairway centerline projection ──────────────────────────────
// Express a point in the back-tee→green frame: along (yds from tee toward green)
// + offset (signed perpendicular, yds). Used to crowdsource the fairway line
// independent of where any individual player's ball is.
const _frame = (T, G) => {
  const lat0 = (T.lat + G.lat) / 2
  const mLat = 111320, mLng = 111320 * Math.cos((lat0 * Math.PI) / 180)
  let ux = (G.lng - T.lng) * mLng, uy = (G.lat - T.lat) * mLat
  const ulen = Math.hypot(ux, uy)
  return ulen ? { mLat, mLng, ux: ux / ulen, uy: uy / ulen } : null
}
export function alongOffset(T, G, P) {
  const f = _frame(T, G)
  if (!f) return null
  const px = (P.lng - T.lng) * f.mLng, py = (P.lat - T.lat) * f.mLat
  return { along: (px * f.ux + py * f.uy) / 0.9144, offset: (px * f.uy - py * f.ux) / 0.9144 }
}
export function fromAlongOffset(T, G, alongYds, offsetYds) {
  const f = _frame(T, G)
  if (!f) return null
  const am = alongYds * 0.9144, om = offsetYds * 0.9144
  const x = am * f.ux + om * f.uy, y = am * f.uy - om * f.ux
  return { lat: T.lat + y / f.mLat, lng: T.lng + x / f.mLng }
}

// ── Segmented hole-axis bearing (D-314 item 5a, pure + unit-tested) ──────────
// Orient the follow camera along the HOLE CORRIDOR, not the player→green vector
// (which swings when the player is off-line, making the green look sideways and
// lagging the turn on a dogleg). `axis` is the ordered polyline of the hole:
// [tee, ...bend/dogleg point(s), green]. We pick the segment the player is
// CURRENTLY traversing — the first leg the player has not yet projected past —
// and return THAT leg's compass bearing. Progress is measured by projecting the
// player onto each leg's own axis (via alongOffset), so a dogleg turns the view
// down the correct leg the moment the player rounds the bend rather than facing
// a compromise/OB direction until they reach the marker. A straight hole has one
// segment and this reduces to bearingDeg(tee, green). Returns a bearing in
// [0,360), or null when the axis has fewer than two valid points.
export function segmentBearing(axis, player) {
  const pts = (axis || []).filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng))
  if (pts.length < 2) return null
  if (!player || !Number.isFinite(player.lat) || !Number.isFinite(player.lng)) {
    return bearingDeg(pts[0].lat, pts[0].lng, pts[1].lat, pts[1].lng)
  }
  for (let i = 0; i < pts.length - 1; i++) {
    const A = pts[i], B = pts[i + 1]
    const segLen = distanceYards(A.lat, A.lng, B.lat, B.lng)
    if (!(segLen > 0)) continue
    // Projected fraction of the player along THIS leg (0 = at A, 1 = at B).
    // The current leg is the first one the player has not yet fully passed.
    const ao = alongOffset(A, B, player)
    const frac = ao ? ao.along / segLen : 0
    if (frac < 1) return bearingDeg(A.lat, A.lng, B.lat, B.lng)
  }
  // Past the projection of every leg → use the final (green-ward) leg.
  const A = pts[pts.length - 2], B = pts[pts.length - 1]
  return bearingDeg(A.lat, A.lng, B.lat, B.lng)
}

// ── Auto-aim target advance (D-314 item 4, pure + unit-tested) ───────────────
// How far AHEAD the aim target sits, measured ALONG the player→green axis (the
// projection of player→target onto player→green, in yards; positive = ahead
// toward the green, negative = behind the player). The old advance rule compared
// the STRAIGHT player→target distance against the player→green distance, so a
// target offset to the side (a fairway/dogleg aim point, or one the walk left
// abreast of) kept a large straight distance and never tripped the threshold
// until the player was WELL PAST it. Projecting onto the axis measures true
// forward progress and is unaffected by lateral offset.
export function targetAheadYds(player, green, target) {
  if (!player || !green || !target) return null
  const ao = alongOffset(player, green, target)
  return ao ? ao.along : null
}
// Advance the auto-target when it has fallen into the nearer `ratio` fraction of
// the remaining player→green shot ALONG the hole axis — i.e. it is no longer far
// enough ahead to be a useful aim point. Because `ratio` > 0 and the target
// starts ahead, this fires while `aheadYds` is still positive, i.e. BEFORE the
// player reaches (let alone passes) the target — deterministic, and independent
// of the follow-camera mode.
export function shouldAdvanceTarget(aheadYds, playerToGreen, ratio = TARGET_MIN_RATIO) {
  if (aheadYds == null || playerToGreen == null || !(playerToGreen > 0)) return false
  return aheadYds < ratio * playerToGreen
}

// ── Focus/recenter button (F-009-T9 item 2) ─────────────────────────────────
// The D-314 item-2 nextFocusMode multi-tap cycle (tap 1 = center on player,
// tap 2 = whole-hole overview, alternating) was REMOVED here. On-course it
// regressed the focus button: a stale cycle state left the green off-screen
// until the 3rd/4th tap. The button now does ONE thing on a tap — the anchored
// player+green fit (fitPlayerAnchored → anchoredFitZoom) — and the whole-hole
// overview moved to an explicit long-press, so the first tap always frames
// correctly. The behavior is a source-scan guard + on-course check.

// ── Map-camera geometry helpers (pure, unit-tested) ─────────────────────────
// Mapbox zoom whose vertical extent shows `spanMeters` across `viewportPx`
// pixels at the given latitude. Mirrors the Web-Mercator ground-resolution
// formula Mapbox uses (78271.517 m per pixel at zoom 0 on the equator, halving
// each level, scaled by cos(lat)). Callers clamp to their min/max zoom.
export function metersToZoom(spanMeters, viewportPx, lat) {
  if (!(spanMeters > 0) || !(viewportPx > 0)) return 0
  const mpp = spanMeters / viewportPx
  return Math.log2((78271.517 * Math.cos((lat * Math.PI) / 180)) / mpp)
}

// ── Anchored rotation-aware fit (the ONE camera recipe) ──────────────────────
// The single source of truth for the on-course fit camera. Measures a set of
// `points` in the hole's own rotated frame (along the `bearing`, across it),
// then fits that length/width to the usable viewport (viewport minus the
// top/bottom reserves and side padding), anchored HORIZONTALLY on `anchor` so
// the anchor point stays centered across-frame and the fit spreads equally
// either side of it. The beloved LOAD view (cameraForHole) and the FOLLOW/FOCUS
// view (fitPlayerAnchored) BOTH call this, so they can never diverge again —
// the round-5 bounds-fit path (excessive zoom-out + green shoved to a
// corner) is gone. Returns { center:[lng,lat], zoom, bearing } or null when
// there are no valid points.
//   • anchor        — held horizontally centered (the tee on load, the player on
//                      follow); across-offset measured from it so W is symmetric.
//   • topReservePx / bottomReservePx — screen chrome the fit must clear.
//   • widthFloorFrac / widthCapM     — minimum lateral width so centerline-only
//                      holes stay pannable (max(W_raw, min(H*frac, cap))).
//   • alongBias      — shift the center down-corridor toward the anchor edge for
//                      cushion (the TEE_BIAS on load; 0 on follow).
//   • zoomFloor / maxZoom — clamp how far OUT / IN the fit may go.
// PURE: no Mapbox, no DOM — the viewport is passed in (vw/vh), so it unit-tests
// and the golden-parity test can prove it reproduces the legacy load view.
export function anchoredFitZoom({ points, anchor, bearing, vw, vh, topReservePx, bottomReservePx, sidePadPx = 24, widthFloorFrac = 0.6, widthCapM = 250, alongBias = 0, zoomFloor = 0, maxZoom = 20 }) {
  const pts = (points || []).filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng))
  if (pts.length === 0) return null
  const lat0 = pts.reduce((s, p) => s + p.lat, 0) / pts.length
  const lng0 = pts.reduce((s, p) => s + p.lng, 0) / pts.length
  const mLat = 111320, mLng = 111320 * Math.cos((lat0 * Math.PI) / 180)
  const br = (bearing * Math.PI) / 180, cosB = Math.cos(br), sinB = Math.sin(br)
  let minA = Infinity, maxA = -Infinity, minC = Infinity, maxC = -Infinity
  for (const p of pts) {
    const x = (p.lng - lng0) * mLng, y = (p.lat - lat0) * mLat
    const a = x * sinB + y * cosB, c = x * cosB - y * sinB
    if (a < minA) minA = a; if (a > maxA) maxA = a
    if (c < minC) minC = c; if (c > maxC) maxC = c
  }
  let anchorC = (minC + maxC) / 2
  if (anchor && Number.isFinite(anchor.lat)) {
    const ax = (anchor.lng - lng0) * mLng, ay = (anchor.lat - lat0) * mLat
    anchorC = ax * cosB - ay * sinB
  }
  const H = Math.max(maxA - minA, 1)
  const W_raw = 2 * Math.max(maxC - anchorC, anchorC - minC)
  const W = Math.max(W_raw, Math.min(H * widthFloorFrac, widthCapM), 1)
  const uw = Math.max(vw - 2 * sidePadPx, 50)
  const uh = Math.max(vh - topReservePx - bottomReservePx, 50)
  const mpp = Math.max(W / uw, H / uh)
  const zoom = Math.max(zoomFloor, Math.min(maxZoom, Math.log2((78271.517 * Math.cos((lat0 * Math.PI) / 180)) / mpp)))
  const cA = (minA + maxA) / 2 - alongBias * (maxA - minA), cC = anchorC
  const cx = cA * sinB + cC * cosB, cy = cA * cosB - cC * sinB
  return { center: [lng0 + cx / mLng, lat0 + cy / mLat], zoom, bearing }
}

// Point a fraction `t` of the way from a to b (0 = a, 1 = b), linear in lat/lng
// — accurate enough over the sub-kilometre span of a golf hole. Used to bias
// the follow camera toward the green and to re-project the aim target.
export function weightedPoint(a, b, t) {
  return { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t }
}

// Linear interpolation between two {lng,lat} points — drives the marker glide
// tween that makes the player dot slide between fixes instead of teleporting.
export function lerpLngLat(from, to, t) {
  return { lng: from.lng + (to.lng - from.lng) * t, lat: from.lat + (to.lat - from.lat) * t }
}

// Padded lat/lng box around a set of hole points — the Mapbox maxBounds pan
// limit. `marginMeters` is added on every side so the green (plus a walking
// margin past it) stays reachable at ANY zoom, while the rest of the course
// stays off-limits (V1 intentionally prevents scanning the whole course).
// Returns { sw:[lng,lat], ne:[lng,lat] } plus scalar edges, or null when empty.
export function holeBounds(points, marginMeters = 130) {
  const pts = (points || []).filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng))
  if (pts.length === 0) return null
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity
  for (const p of pts) {
    if (p.lat < minLat) minLat = p.lat
    if (p.lat > maxLat) maxLat = p.lat
    if (p.lng < minLng) minLng = p.lng
    if (p.lng > maxLng) maxLng = p.lng
  }
  const lat0 = (minLat + maxLat) / 2
  const dLat = marginMeters / 111320
  const dLng = marginMeters / (111320 * Math.cos((lat0 * Math.PI) / 180))
  return {
    sw: [minLng - dLng, minLat - dLat],
    ne: [maxLng + dLng, maxLat + dLat],
    minLng: minLng - dLng, minLat: minLat - dLat,
    maxLng: maxLng + dLng, maxLat: maxLat + dLat,
  }
}

// ── GPS fix filter (pure state machine, unit-tested) ────────────────────────
// Cleans the raw geolocation stream BEFORE it moves the dot:
//   1. Accuracy gate — drop fixes whose reported accuracy is worse than
//      `accuracyGateM` metres, UNLESS nothing has been accepted yet (so the dot
//      still appears on a cold start / weak-signal tee box, the "only fix
//      available" case).
//   2. EMA low-pass — an exponential moving average on lat/lng so a player
//      standing still holds steady instead of dancing on GPS jitter.
// `push(fix)` returns the smoothed {lat,lng,accuracy} to render, or null when
// the fix was dropped. Deterministic apart from its own internal state, so it
// unit-tests by feeding a sequence of fixes.
export function makeGpsFilter({ accuracyGateM = 25, emaAlpha = 0.35 } = {}) {
  let smoothed = null
  return {
    push(fix) {
      if (!fix || !Number.isFinite(fix.lat) || !Number.isFinite(fix.lng)) return null
      const acc = fix.accuracy
      const accepted = smoothed != null
      // Gate only once we have a fix to keep showing; the first fix always lands.
      if (accepted && Number.isFinite(acc) && acc > accuracyGateM) return null
      if (!accepted) {
        smoothed = { lat: fix.lat, lng: fix.lng }
      } else {
        smoothed = weightedPoint(smoothed, fix, emaAlpha)
      }
      return { lat: smoothed.lat, lng: smoothed.lng, accuracy: acc }
    },
    reset() { smoothed = null },
  }
}
